import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { UserPlus, Clock, RefreshCw, Loader2, CheckCircle2, Users, LogOut } from 'lucide-react';
import { visitorAPI } from '../../services/api';
import { toast } from 'sonner';

function timeAgo(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function formatTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString('en-IN', {
      hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata',
    });
  } catch { return ''; }
}

export function VisitorRegistrationCard({ setShowVisitorForm }) {
  const [pending, setPending] = useState([]);
  const [activeToday, setActiveToday] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(null);
  const [checkingOut, setCheckingOut] = useState(null);
  const [showActiveModal, setShowActiveModal] = useState(false);

  const load = useCallback(async () => {
    try {
      const [pendRes, allRes] = await Promise.allSettled([
        visitorAPI.pending(),
        visitorAPI.getAll(),
      ]);
      if (pendRes.status === 'fulfilled') {
        setPending(pendRes.value.data?.visitors || []);
      }
      if (allRes.status === 'fulfilled') {
        const active = (allRes.value.data || []).filter(v => !v.exit_time);
        setActiveToday(active);
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  // Poll every 5 seconds for fresh pending registrations
  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [load]);

  const handleApprove = async (visitor) => {
    setApproving(visitor.id);
    try {
      await visitorAPI.approve(visitor.id);
      toast.success(`Approved entry for ${visitor.name}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve');
    } finally {
      setApproving(null);
    }
  };

  const handleCheckout = async (visitor) => {
    setCheckingOut(visitor.id);
    try {
      await visitorAPI.checkout(visitor.id);
      toast.success(`${visitor.name} checked out`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to check out');
    } finally {
      setCheckingOut(null);
    }
  };

  return (
    <Card className="shadow-xl border-0" data-testid="visitor-registration-card">
      <CardHeader className="bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-t-xl">
        <CardTitle className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-2"><UserPlus className="w-5 h-5" />Visitor Registration</span>
          <button data-testid="refresh-pending-btn" onClick={load} className="text-white/80 hover:text-white" title="Refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 sm:p-6">
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Register visitors without pre-approved vehicle registration.
          </p>
          <Button
            data-testid="register-visitor-button"
            onClick={() => setShowVisitorForm(true)}
            className="w-full h-20 sm:h-32 bg-amber-500 hover:bg-amber-600 text-base sm:text-lg font-semibold"
          >
            <UserPlus className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
            Register New Visitor
          </Button>
          <div className="grid grid-cols-2 gap-3 sm:gap-4 pt-2">
            <div data-testid="pending-count-tile" className="p-3 bg-amber-50 rounded-lg text-center border border-amber-200">
              <p className="text-3xl sm:text-4xl font-bold text-amber-600">{pending.length}</p>
              <p className="text-sm text-slate-600">Pending Approvals</p>
            </div>
            <button
              type="button"
              data-testid="active-today-tile"
              onClick={() => setShowActiveModal(true)}
              className="p-3 bg-emerald-50 rounded-lg text-center border border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 transition-colors cursor-pointer"
              title="View active visitors"
            >
              <p className="text-3xl sm:text-4xl font-bold text-emerald-600">{activeToday.length}</p>
              <p className="text-sm text-slate-600">Active Today</p>
              <p className="text-[10px] text-emerald-600 mt-0.5 font-medium">Click to view →</p>
            </button>
          </div>

          {/* Pending visitor list */}
          <div className="pt-2 border-t">
            <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2 flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-amber-600" />
              Awaiting Resident Approval
              <span className="text-[10px] text-slate-400 font-normal normal-case ml-1">(auto-refreshes)</span>
            </p>
            {loading && pending.length === 0 ? (
              <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-amber-500" /></div>
            ) : pending.length === 0 ? (
              <p className="text-center text-sm text-slate-400 italic py-3">No visitors pending approval.</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {pending.map(v => (
                  <div key={v.id} data-testid={`pending-visitor-${v.id}`} className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-sm text-slate-800 truncate">{v.name}</p>
                        <p className="text-xs text-slate-600 truncate">
                          Villa <span className="font-mono font-semibold">{v.villa_number}</span>
                          {v.plate_number && <> · <span className="font-mono">{v.plate_number}</span></>}
                          {v.phone && <> · {v.phone}</>}
                        </p>
                        {v.purpose && <p className="text-[11px] text-slate-500 italic truncate">"{v.purpose}"</p>}
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className="bg-amber-500 text-white text-[10px]">Pending</Badge>
                          <span className="text-[10px] text-slate-500">{timeAgo(v.entry_time)}</span>
                        </div>
                      </div>
                      <Button
                        data-testid={`approve-pending-${v.id}`}
                        size="sm"
                        onClick={() => handleApprove(v)}
                        disabled={approving === v.id}
                        className="bg-emerald-600 hover:bg-emerald-700 shrink-0 h-8 px-2"
                        title="Mark as approved (override resident approval)"
                      >
                        {approving === v.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </CardContent>

      <Dialog open={showActiveModal} onOpenChange={setShowActiveModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="active-visitors-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-emerald-600" />
              Active Visitors Today ({activeToday.length})
            </DialogTitle>
            <DialogDescription>
              Visitors currently inside the community who haven't checked out yet.
            </DialogDescription>
          </DialogHeader>
          {activeToday.length === 0 ? (
            <p className="text-center text-sm text-slate-400 italic py-6" data-testid="active-visitors-empty">
              No active visitors right now.
            </p>
          ) : (
            <div className="space-y-2">
              {activeToday.map((v) => (
                <div
                  key={v.id}
                  data-testid={`active-visitor-${v.id}`}
                  className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-sm text-slate-800 truncate">{v.name}</p>
                      <p className="text-xs text-slate-600 truncate">
                        Villa <span className="font-mono font-semibold">{v.villa_number}</span>
                        {v.plate_number && v.plate_number !== '-' && <> · <span className="font-mono">{v.plate_number}</span></>}
                        {v.phone && v.phone !== '-' && <> · {v.phone}</>}
                      </p>
                      {v.purpose && v.purpose !== '-' && (
                        <p className="text-[11px] text-slate-500 italic truncate">"{v.purpose}"</p>
                      )}
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className={`text-[10px] ${
                          v.status === 'approved' ? 'bg-emerald-500' : 'bg-amber-500'
                        } text-white`}>
                          {v.status === 'approved' ? 'Approved' : 'Pending'}
                        </Badge>
                        <span className="text-[10px] text-slate-500">
                          Entry: {formatTime(v.entry_time)} · {timeAgo(v.entry_time)}
                        </span>
                      </div>
                    </div>
                    <Button
                      data-testid={`checkout-active-${v.id}`}
                      size="sm"
                      onClick={() => handleCheckout(v)}
                      disabled={checkingOut === v.id}
                      variant="outline"
                      className="border-red-300 text-red-600 hover:bg-red-50 shrink-0 h-8 px-2"
                      title="Check out (mark visitor as departed)"
                    >
                      {checkingOut === v.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <><LogOut className="w-3.5 h-3.5 mr-1" />Checkout</>
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
